import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import {ReportSummaryRowComponent} from './report-summary-row/report-summary-row.component';
import {ActivatedRoute, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {combineLatest, EMPTY, from, fromEvent, merge, Observable, Subject} from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  map,
  share,
  switchAll,
  take,
  takeUntil,
  tap,
  windowToggle
} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';

import * as moment from 'moment/moment';

import {ReportAnalyticsRequest} from './analytics/report-analytics.interfaces';
import {ElasticQueryService} from './elasticquery.service';
import {Preview} from './preview';
import {Report} from './report';
import {ReportGroupedResultItemComponent} from './report-grouped-result-item/report-grouped-result-item.component';
import {setOutputFiltersPosition} from './report-top-card/report-top-card.component';
import {
  GroupedItem,
  PreviewContractReport,
  PreviewInput,
  PreviewSettings,
  ReportCategories,
  ReportEntity
} from './reports.interfaces';
import {ReportsService} from './reports.service';

import Handsontable from 'handsontable';
import {ToastrService} from 'ngx-toastr';
import {GpHotTableColumn, HotTableDataType} from '../+mass-data-update/mass-data-update.interfaces';
import {DR_DATE_FORMAT} from '../app.constants';
import {Destroyable} from '../destroyable.component';
import {AnyData} from '../shared/api';
import {RolesService} from '../shared/authentication/roles/roles.service';
import {EmpFile} from '../shared/employees/employees.model';
import {HotTableDirective} from '../shared/hot-table/hot-table.directive';
import {LocalizationService} from '../shared/localization/localization.service';
import {GPTranslateService} from '../shared/localization/translate.service';
import {ModalService} from '../shared/modal';
import {TreeNode} from '../shared/ui';
import {PopoverService} from '../shared/ui/popover.service';
import {TreeItem} from '../shared/ui/tree-multi-select/treeview.component';
import {User, UserService} from '../shared/user';
import {Files} from '../shared/utils/files';
import {isPresent} from '../shared/utils/lang';

@Component({
  selector: 'gp-report-manage',
  templateUrl: './report-manage.component.html',
  styleUrls: ['./report.scss', './report-manage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ReportManageComponent extends Destroyable implements OnInit, AfterViewInit {
  public static ACCESS_NAME = 'ReportManage';
  popoverData$ = this.popoverService.popoverData$;

  public report: Report;

  public MAX_FILTER_COUNT = 10;
  public MAX_RESULT_COUNT = 100;
  public previewLoading = false;
  public exportInProgress = false;
  public GP_ENTITY_PATH_SUFFIX = '_GP__ENTITY_PATH';

  @ViewChild('reportFilterSidePanel') public reportFilterSidePanel: ElementRef;
  @ViewChild(HotTableDirective) public hotTable: HotTableDirective;
  @ViewChildren(HotTableDirective) public allHotTables: QueryList<HotTableDirective>;
  @ViewChildren(ReportGroupedResultItemComponent) private groupedResultItems: QueryList<ReportGroupedResultItemComponent>;
  @ViewChild('resizeLine') public resizeLine: ElementRef;
  @ViewChild('reportTopCardRef', {static: false}) reportTopCard!: ElementRef;
  @ViewChild('fileDetailPopover', {static: true}) public fileDetailPopover: TemplateRef<unknown>;
  @ViewChild(ReportSummaryRowComponent) private summaryRow: ReportSummaryRowComponent;
  public user: User;
  public latestPreviewData: PreviewInput;
  public isEmptyTable = false;
  public activeReportId: number;
  public previewData: any;
  public isSidePanelCollapsed = false;
  public enabledFields: string[];
  public contractId: number;

  public reportsList: ReportEntity[];
  public reportsTree: TreeNode<TreeItem>[];
  public resizeLeft = 420;
  public resizeFilterColumnBlockHeight = 0;

  private language: string;
  public activeReportName: string;
  public DYNAMIC_REPORTS = '_dynamic_reports';

  public exportDropdownCollapsed = false;

  public isPreviewLoaded = false;
  public file: EmpFile = null;
  public hasWriteAccessOnGloReg101 = false;
  public get totalRowsCount(): number {
    return this.previewData?.data?.length ?? 0;
  }
  public isReportStandard = false;
  public perPage = 5000;

  public currentPage = 0;
  public totalRecordsFromBackend = 0;
  public isLoadingMore = false;
  public hasMoreData = false;
  public allRecordsLoaded = false;
  public searchAfterToken: string | null = null;
  public cancelSearch$ = new Subject<void>();
  public accumulatedData: Record<string, unknown>[] = [];
  public accumulatedContractIds: number[] = [];
  public isColumnSorted = false;

  private _groupSortedItems = false;
  public currentSortConfigs: any[] = [];

  public groupedDisplayItems: GroupedItem[] = [];

  public aiAnalyticsEnabled = false;
  public analyticsRequest: ReportAnalyticsRequest | null = null;
  public analyticsRefreshToken = 0;

  public get groupSortedItems(): boolean {
    return this._groupSortedItems;
  }

  public set groupSortedItems(value: boolean) {
    this._groupSortedItems = value;
    this.applyOrRemoveGrouping();
  }

  private columnSortHook: ((...args: any[]) => void) | null = null;

  /** Exposed so the template can pass it to <gp-report-summary-row>. */
  public summaryColumns: AnyData[] = [];
  public summaryData: Record<string, unknown>[] = [];

  public get loadedItemsCount(): number {
    return this.accumulatedData?.length || 0;
  }

  public get totalItemsDisplay(): number {
    return this.totalRecordsFromBackend;
  }

  public get isGroupSortedItemsDisabled(): boolean {
    return this.hasMoreData || !this.isColumnSorted;
  }

  public get groupSortedItemsTooltip(): string {
    return this.isGroupSortedItemsDisabled
      ? this.gpTranslateService.instant('REPORTS.sort_column_to_enable_grouping')
      : '';
  }

  public get isPreviewBtnDisabled(): boolean {
    return !this.report.columns.length ||
      !this.report.reportEntity.beginDate ||
      !this.report.reportEntity.endDate ||
      this.tooMuchFiltersSet() ||
      this.tooMuchResultsSet();
  }

  public get isExportBtnDisabled(): boolean {
    return this.isPreviewBtnDisabled && !this.isPreviewLoaded;
  }

  public get isCsvExportTooltipVisible(): boolean {
    return !this.isPreviewLoaded || this.isEmptyTable || !this.previewData || this.hasMoreData;
  }

  public trackByGroupItem(index: number, item: GroupedItem): any {
    return item.value ?? index;
  }

  constructor(public reportsService: ReportsService,
              private gpTranslateService: GPTranslateService,
              private router: Router,
              private route: ActivatedRoute,
              private userService: UserService,
              private modalService: ModalService,
              private cd: ChangeDetectorRef,
              private localization: LocalizationService,
              private preview: Preview,
              private rolesService: RolesService,
              private elasticQueryService: ElasticQueryService,
              private toasterService: ToastrService,
              private popoverService: PopoverService,
              renderer: Renderer2,
              viewContainerRef: ViewContainerRef,
              private translateService: TranslateService) {
    super();

    this.preview.setRenderer(renderer);
    this.preview.setViewContainerRef(viewContainerRef);
  }

  public get isInitialState(): boolean {
    return !this.previewData && !this.activeReportId && !this.isEmptyTable;
  }


  public ngOnInit(): void {
    combineLatest([this.route.params, this.route.queryParams])
      .pipe(
        debounceTime(0),
        tap(([params, queryParams]) => {
          this.activeReportId = parseInt(params['id'], 10);
          this.isReportStandard = queryParams['isStandard'] === 'true';
          if (this.activeReportId) {
            this.selectReport(this.activeReportId, this.isReportStandard);
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.popoverData$
      .pipe(
        tap(data => {
          if (data) {
            const fileDescription = data[0];

            this.file = {
              id: fileDescription.id.toString(),
              zone: fileDescription.tags['zone'],
              groupZone: fileDescription.tags['groupZone'],
              filename: fileDescription.name,
              size: fileDescription.size,
              updateDate: fileDescription.updateDate,
              uploadDate: fileDescription.creationDate,
              initiatorName: fileDescription.tags['initiatorName']
            };
          }
        }),
        catchError(() => {
          this.toasterService.error(this.gpTranslateService.instant('COMMON.something_went_wrong'));
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.checkWriteAccessOnRootCliReg();

    this.userService.getUser()
      .pipe(
        tap((user: User) => {
          this.user = user;
          if (!this.report) {
            this.createEmptyReport();
          }
          this.getReports();
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.reportsService.currentFields
      .pipe(
        tap(() => {
          this.cd.markForCheck();
          this.resizeFilterColumnCardHeight();
        }),
        takeUntil(this.componentDestroyed$),
      ).subscribe();

    this.reportsService.$reportTimeTypeChanged
      .subscribe((reportTimeType: string) => {
        this.resizeFilterColumnCardHeight();
      });

    this.reportsService.$reportUpdated
      .pipe(
        tap(() => {
          this.handleCriteriaChange();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    merge(this.localization.changeDateFormatEvent,
      this.localization.changeNumberFormatEvent)
      .pipe(
        share(),
        tap(() => this.refreshPreview()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    this.resizeMouseDown();

    if (this.fileDetailPopover) {
      this.preview.setDetailsPopoverContent(this.fileDetailPopover);
    }
  }

  // -------------------------------------------------Reports------------------------------------------------------------

  public onUpdateReportList() {
    if (!this.user) {
      return;
    }
    this.language = this.language || this.user.language;
    this.getReports();
  }

  public onSetEnabledFields(fields: string[]) {
    this.enabledFields = fields;
  }

  public createEmptyReport() {
    if (!this.user) {
      return;
    }
    this.language = this.language || this.user.language;
    this.report = new Report(this.getEmptyReportEntity());

    this.clearLoadMoreData();
  }

  public getReports(): void {
    if (!this.user) {
      return;
    }

    this.reportsService.getReportsList()
      .pipe(
        tap(response => {
          const newResponse = this.setReportType(this.user.id, response);
          this.reportsList = newResponse;
          this.reportsTree = this.createTree(newResponse);

          this.updateReportTitles();
        }),
        finalize(() => {
          if (this.activeReportId) {
            this.selectReport(this.activeReportId, this.isReportStandard);
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private checkWriteAccessOnRootCliReg(): void {
    this.reportsService.checkWriteAccessOnRootCliReg()
      .pipe(
        tap(cliRegDTO => {
          if (cliRegDTO) {
            this.hasWriteAccessOnGloReg101 = cliRegDTO.write;
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private isReportCreatedBySelf(userID: number, report: ReportEntity): boolean {
    return report.authorId === userID;
  }

  private isReportShared(userID: number, report: ReportEntity): boolean {
    return report.cliUserIds.some(id => id !== userID);
  }

  public setReportType(userID: number, reports: ReportEntity[]): ReportEntity[] {
    const currentLocale = this.user.data.attributes.locale;
    reports.forEach((report, index) => {

      if (report.isPublic) {
        report.parentValue = ReportCategories.PUBLIC_REPORTS;
      } else if (report.isStandard) {
        report.parentValue = ReportCategories.STANDARD_REPORTS;
      } else if (this.isReportShared(userID, report)) {
        report.parentValue = ReportCategories.SHARED_REPORTS;
      } else if (this.isReportCreatedBySelf(userID, report)) {
        report.parentValue = ReportCategories.MY_REPORTS;
      }
    });

    reports.sort((a, b) => {
      // Get title for report A with fallbacks
      const titleA = a.title || '';

      // Get title for report B with fallbacks
      const titleB = b.title || '';

      return titleA.localeCompare(titleB);
    });

    return reports.filter(report => report.parentValue);
  }

  public createTree(filterItems: ReportEntity[]): TreeNode<TreeItem>[] {

    const parents = Object.values(ReportCategories).map(category => ({
      text: category,
      value: category,
      data: null,
      selectable: false,
      visible: true,
      children: []
    }));

    const lookupMap: Map<string, TreeNode<TreeItem>> = new Map<string, TreeNode<TreeItem>>();
    const zoneTypeTree = new Array<TreeNode<TreeItem>>();

    // Add parent categories to the tree first
    parents.forEach(parent => {
      zoneTypeTree.push(parent);
      lookupMap.set(parent.value as string, parent);
    });

    // Add report items as children to their respective parent categories
    filterItems.forEach((item: ReportEntity) => {
      const reportNode: TreeNode<TreeItem> = {
        text: this.getReportTitleWithEditIcon(item),
        value: this.getIdWithSuffixByStandardCheck(item),
        data: null,
        selectable: true,
        visible: true,
        children: []
      };

      const parentNode = lookupMap.get(item.parentValue);
      if (parentNode) {
        parentNode.children.push(reportNode);
      } else {
        // Fallback if no parent category found
        zoneTypeTree.push(reportNode);
      }
    });

    return zoneTypeTree;
  }

  private getIdWithSuffixByStandardCheck(item: ReportEntity) {
    return item.isStandard ? `std:${item.id}` : `${item.id}`;
  }

  private getReportTitleWithEditIcon(item: ReportEntity): string {
    const text = this.gpTranslateService.instant(item.title);

    const isEditable = this.isEditableForMyReports(item) ||
      (this.isEditableForSharedOrPublicReports(item) && this.isReportSharedWithUser(item)) ||
      this.isEditableForStandardReports(item);

    return isEditable ? `${text} <i class="fa fa-light fa-pen-to-square edit-icon"></i>` : text;
  }

  private isEditableForMyReports(item: ReportEntity): boolean {
    return this.isReportParentMyReports(item);
  }

  private isEditableForSharedOrPublicReports(item: ReportEntity): boolean {
    return this.isReportParentSharedOrPublicReports(item) && this.isReportAuthorOrEditingAllowed(item);
  }

  private isEditableForStandardReports(item: ReportEntity): boolean {
    return this.isReportParentStandardReports(item) &&
      this.isAdminConfigUser() && this.hasWriteAccessOnGloReg101;
  }

  private isReportSharedWithUser(item: ReportEntity) {
    return item.cliUserIds.includes(this.user.id);
  }

  private isReportAuthorOrEditingAllowed(item: ReportEntity) {
    return item.authorId === this.user.id || item.editingAllowed;
  }

  private isAdminConfigUser() {
    return this.rolesService.hasRole('CONFIGURATION');
  }

  private isReportParentStandardReports(item: ReportEntity) {
    return item.parentValue === ReportCategories.STANDARD_REPORTS;
  }

  private isReportParentSharedOrPublicReports(item: ReportEntity) {
    return item.parentValue === ReportCategories.SHARED_REPORTS ||
      item.parentValue === ReportCategories.PUBLIC_REPORTS;
  }

  private isReportParentMyReports(item: ReportEntity) {
    return item.parentValue === ReportCategories.MY_REPORTS;
  }

  public selectReport(reportId: number, isReportStandard = false): void {
    if (!isPresent(reportId)) {
      return;
    }

    this.reportsService.getReport(reportId, isReportStandard)
      .subscribe((element: ReportEntity) => {
        this.report = new Report(element);
        this.reportsService.$reportUpdated.next(this.report);
        this.activeReportName = this.report.reportEntity.title;
        setTimeout(() => {
          this.reportsService.markAsPristine();
          this.cd.markForCheck();
        });
        this.previewData = null;
        this.isPreviewLoaded = false;
        this.isEmptyTable = false;

        this.clearLoadMoreData();

        this.cd.markForCheck();
      });
  }

  public getEmptyReportEntity(): ReportEntity {
    return {
      id: null,
      cliCoreID: this.user.getCliCore().id,
      authorId: this.user.id,
      beginDate: '',
      endDate: '',
      dateOption: '',
      filterFields: [],
      outputFields: [],
      cliUserIds: [],
      timeFilter: null,
      links: null,
      editingAllowed: false,
      isPublic: false,
      isStandard: false,
      title: null,
      massActionsEnabled: false
    };
  }

  public setLanguage(language: string): void {
    this.language = language;
    this.refreshPreview();
  }

  // -------------------------------------------------Preview------------------------------------------------------------
  public generatePreview(data: PreviewInput): void {
    this.latestPreviewData = cloneDeep(data);

    this.preview.generatePreview(data,
      this.reportFilterSidePanel.nativeElement.offsetHeight - 90,
      this.reportsService.zonesMap, this.language)
      .pipe(
        tap((generatePreviewResult: any) => {
          this.isEmptyTable = !generatePreviewResult;

          this.setTranslatedTotalText();

          if (!this.isEmptyTable) {
            this.previewData = cloneDeep(generatePreviewResult);
            this.processPreviewData(data);
            this.isPreviewLoaded = true;
          }

          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  private processPreviewData(data: PreviewInput): void {
    this.addViewDetailsColumn(data);
    this.applyColumnSortingSetting();
    this.registerColumnSortHook();
    if (this._groupSortedItems && this.currentSortConfigs?.length) {
      this.applyGrouping();
    }
    this.summaryColumns = this.previewData.columns;
    this.summaryData = this.previewData.data;
  }

  private registerColumnSortHook(): void {
    setTimeout(() => {
      if (!this.hotTable?.table) {
        return;
      }
      if (this.columnSortHook) {
        this.hotTable.table.removeHook('afterColumnSort', this.columnSortHook);
      }
      this.columnSortHook = (currentSortConfig: any[], destinationSortConfigs: any[]) => {
        this.isColumnSorted = Array.isArray(destinationSortConfigs) && destinationSortConfigs.length > 0;
        this.currentSortConfigs = destinationSortConfigs || [];
        if (this._groupSortedItems) {
          if (this.isColumnSorted) {
            this.applyGrouping();
          } else {
            this.restoreFromGrouping();
          }
        }
        this.cd.markForCheck();
      };
      this.hotTable.table.addHook('afterColumnSort', this.columnSortHook);

      if (this.currentSortConfigs?.length) {
        const sortPlugin = this.hotTable.table.getPlugin('columnSorting');
        if (sortPlugin?.sort) {
          sortPlugin.sort(this.currentSortConfigs[0]);
        }
      }
    });
  }

  private applyOrRemoveGrouping(): void {
    if (!this.previewData || this.isEmptyTable) {
      return;
    }
    if (this._groupSortedItems) {
      this.applyGrouping();
    } else {
      this.restoreFromGrouping();
      this.registerColumnSortHook();
    }
    this.cd.markForCheck();
  }

  private applyGrouping(): void {
    if (!this.currentSortConfigs?.length) {
      return;
    }

    const primarySort = this.currentSortConfigs[0];
    const sortedColIndex = primarySort.column;

    const dataColumns = this.previewData.columns.slice(0, -1);
    const col = dataColumns[sortedColIndex];
    if (!col?.data) {
      return;
    }

    const dataField: string = col.data;
    const rawHeader: string = this.previewData.headers[sortedColIndex] || '';
    const columnName = rawHeader.replace(/<[^>]+>/g, '').trim();

    const existingExpandedMap = new Map(
      this.groupedDisplayItems.map(item => [item.value, item.expanded])
    );

    const groupMap = new Map<string, { rows: Record<string, unknown>[]; contractIds: number[]; count: number }>();

    this.previewData.data.forEach((row: Record<string, unknown>, i: number) => {
      const key = String(row?.[dataField] ?? '');
      if (!groupMap.has(key)) {
        groupMap.set(key, { rows: [], contractIds: [], count: 0 });
      }
      const group = groupMap.get(key);
      group.rows.push(row);
      group.contractIds.push(this.previewData.contractIds?.[i]);
      group.count++;
    });

    const sortOrder: string = primarySort.sortOrder || 'asc';
    const entries = Array.from(groupMap.entries()).sort(([a], [b]) => {
      const cmp = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'desc' ? -cmp : cmp;
    });

    this.groupedDisplayItems = entries.map(([value, group]) => ({
      columnName,
      value,
      count: group.count,
      rows: group.rows,
      contractIds: group.contractIds,
      expanded: existingExpandedMap.get(value) ?? false
    }));
  }

  private restoreFromGrouping(): void {
    this.groupedDisplayItems = [];
  }

  private applyColumnSortingSetting(): void {
    if (!this.previewData.options) {
      this.previewData.options = {};
    }
    this.previewData.options.columnSorting = true;

    const lastIndex = this.previewData.columns.length - 1;
    const sortingSettingOf = this.hasMoreData
      ? (col: GpHotTableColumn) => this.withHeaderSortingDisabled(col)
      : (col: GpHotTableColumn) => this.withDefaultHeaderSorting(col);

    this.previewData.columns = this.previewData.columns.map((col: GpHotTableColumn, index: number) =>
      index === lastIndex ? col : sortingSettingOf(col));
  }

  private withHeaderSortingDisabled(col: GpHotTableColumn): GpHotTableColumn {
    return { ...col, columnSorting: { ...this.customComparatorOf(col), headerAction: false, indicator: false } };
  }

  private withDefaultHeaderSorting(col: GpHotTableColumn): GpHotTableColumn {
    const { columnSorting: _columnSorting, ...rest } = col;
    const customComparator = this.customComparatorOf(col);
    return customComparator ? { ...rest, columnSorting: customComparator } : rest;
  }

  private customComparatorOf(col: GpHotTableColumn): { compareFunctionFactory: AnyData } | undefined {
    const columnSorting = col.columnSorting;
    return typeof columnSorting === 'object' && columnSorting.compareFunctionFactory
      ? { compareFunctionFactory: columnSorting.compareFunctionFactory }
      : undefined;
  }


  private addViewDetailsColumn(data: PreviewInput): void {
    const eyeIconColumn: GpHotTableColumn = {
      data: '',
      type: HotTableDataType.TEXT,
      renderer: this.viewEmployeeDetailsButtonRenderer.bind(this),
      readOnly: false,
      columnSorting: { headerAction: false, indicator: false }
    };

    this.previewData.columns = [...this.previewData.columns, eyeIconColumn];
    this.previewData.headers = [...this.previewData.headers, ''];
    this.previewData.contractIds = data.contractIds;
  }

  private setTranslatedTotalText(): void {
    const translatedText = this.gpTranslateService.instant('COMMON.total');
    document.documentElement.style.setProperty('--total-text', `"${translatedText}"`);
  }

  public viewEmployeeDetailsButtonRenderer(...tableArgs: AnyData[]) {
    const [instance, td, row] = tableArgs;
    Handsontable.renderers.HtmlRenderer.apply(instance, tableArgs);

    const physicalRow = this.toPhysicalRow(instance, row);
    const contractId = this.previewData.contractIds[physicalRow];
    const button = document.createElement('button');
    button.style.border = '0';
    button.style.display = 'block';
    button.style.margin = '0 auto';
    button.className = 'btn btn-xs btn-icon white fa fa-light fa-eye';
    button.id = contractId;
    button.addEventListener('click', () => this.showEmployeeDetails(+contractId));

    td.innerHTML = '';
    td.appendChild(button);
    return td;
  }

  private toPhysicalRow(instance: AnyData, visualRow: number): number {
    const physicalRow = instance?.toPhysicalRow?.(visualRow);
    return typeof physicalRow === 'number' && physicalRow >= 0 ? physicalRow : visualRow;
  }

  public showEmployeeDetails(id: number) {
    this.contractId = id;
    this.modalService.openModalWithId('employee-detail');
  }

  public onExport(): void {
    this.initExport();
  }


  public onExportCsv(): void {
    if (!this.hotTable) {
      return;
    }
    const filename = this.fileNameGenerator(this.activeReportName);
    const summaryRow = this.buildCsvSummaryRow();
    const exportPlugin = this.hotTable.table.getPlugin('exportFile');

    this.userService.getUser().pipe(take(1)).subscribe((user: User) => {
      const useCommaDecimal = user.decimalMask === ',';
      const csvSeparator = useCommaDecimal ? ';' : ',';
      const exportData = this.buildExportData(summaryRow, useCommaDecimal);

      exportPlugin.hot.loadData(exportData);
      exportPlugin.downloadFile('csv', {filename, columnHeaders: true, columnDelimiter: csvSeparator});
      exportPlugin.hot.loadData(this.hotTable.data);
    });
  }

  private buildExportData(summaryRow: Record<string, unknown> | null, useCommaDecimal: boolean): any[] {
    const rows = useCommaDecimal
      ? cloneDeep(this.hotTable.data).map(row => this.formatRowForDecimalExport(row))
      : [...this.hotTable.data];

    if (!summaryRow) {
      return rows;
    }
    const formattedSummaryRow = useCommaDecimal
      ? this.formatRowForDecimalExport(cloneDeep(summaryRow))
      : summaryRow;
    return [...rows, formattedSummaryRow];
  }

  private formatRowForDecimalExport(row: Record<string, unknown>): Record<string, unknown> {
    Object.keys(row).forEach(key => {
      if (typeof row[key] === 'number') {
        row[key] = String(row[key]).replace('.', ',');
      }
    });
    return row;
  }

  private buildCsvSummaryRow(): Record<string, unknown> | null {
    const columnSums = this.summaryRow?.columnSums;
    if (!columnSums || !Object.keys(columnSums).length || !this.previewData?.columns?.length) {
      return null;
    }
    const columns = this.previewData.columns;
    const lastIndex = columns.length - 1;
    const row: Record<string, unknown> = {};
    columns.forEach((col, index) => {
      const key = col.data as string;
      if (Object.prototype.hasOwnProperty.call(columnSums, key)) {
        row[key] = columnSums[key];
      } else {
        row[key] = index === lastIndex ? this.gpTranslateService.instant('COMMON.total') : '';
      }
    });
    return row;
  }

  public fileNameGenerator(reportFileName: string): string {
    const reportName = reportFileName ?? '';
    return moment().format(DR_DATE_FORMAT) + this.DYNAMIC_REPORTS + (reportName ? ('_' + reportName.replace(/[\\/:"*?<>|]+/g, '')) : '');
  }

  // -------------------------------------------------Resize-------------------------------------------------------------
  public resizeMouseDown(): boolean {
    fromEvent(document, 'mousemove')
      .pipe(
        finalize(() => {
          if (!this.isSidePanelCollapsed) {
            this.renderAllTables();
          }
        }),
        windowToggle(
          fromEvent(this.resizeLine?.nativeElement, 'mousedown'),
          () => fromEvent(document, 'mouseup')),
        switchAll(),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((event: any) => {
        this.cd.markForCheck();
        const minLeft: number = this.reportFilterSidePanel.nativeElement.getBoundingClientRect().left;
        const maxLeft: number = (window.innerWidth / 3) + minLeft;

        if (event.clientX > maxLeft) {
          this.resizeLeft = maxLeft - minLeft;
        } else if (event.clientX < minLeft + 250) {
          this.resizeLeft = 250;
        } else {
          this.resizeLeft = event.clientX - minLeft;
        }
      });

    return false;
  }

  public toggleSidePanel() {
    this.isSidePanelCollapsed = !this.isSidePanelCollapsed;
    this.resizeLeft = this.isSidePanelCollapsed ? 50 : 420;
    this.renderAllTables();
  }

  private refreshPreview() {
    if (this.latestPreviewData) {
      this.generatePreview(this.latestPreviewData);
    }
  }

  private renderTable() {
    if (!this.hotTable) {
      return;
    }

    setTimeout(() => {
      this.hotTable.table.render();
    });
  }

  private renderAllTables(): void {
    setTimeout(() => {
      if (this.allHotTables) {
        this.allHotTables.forEach(hotTable => {
          if (hotTable?.table) {
            hotTable.table.render();
          }
        });
      }
      if (this.groupedResultItems) {
        this.groupedResultItems.forEach(item => item.renderTable());
      }
    });
  }

  private updateReportTitles() {
    if (this.reportsList) {
      this.reportsList.forEach(report => {
        if (!report.title) {
          report.title = '-';
        }
      });
    }
  }

  private resizeFilterColumnCardHeight() {
    setTimeout(() => {
      if (this.reportTopCard) {
        this.resizeFilterColumnBlockHeight = -1 * this.reportTopCard.nativeElement.offsetHeight - 58;
      }
    }, 0);
  }

  public handleCriteriaChange(): void {
    if (!this.isPreviewLoaded) {
      return;
    }
    this.resetOnCriteriaChange();
  }

  private resetGroupingState(): void {
    this.isColumnSorted = false;
    this._groupSortedItems = false;
    this.groupedDisplayItems = [];
    this.currentSortConfigs = [];
  }

  private clearLoadMoreData(): void {
    this.currentPage = 0;
    this.searchAfterToken = null;
    this.hasMoreData = false;
    this.allRecordsLoaded = false;
    this.totalRecordsFromBackend = 0;
    this.accumulatedData = [];
    this.accumulatedContractIds = [];
    this.resetGroupingState();
  }

  private resetOnCriteriaChange(): void {
    this.cancelSearch$.next();
    this.isLoadingMore = false;
    this.currentPage = 0;
    this.searchAfterToken = null;
    this.hasMoreData = false;
    this.allRecordsLoaded = false;
  }

  public initPreview(): void {
    this.previewLoading = true;
    this.currentPage = 0;
    this.clearLoadMoreData();

    const previewSettings = this.setupPreviewContext();
    if (this.aiAnalyticsEnabled) {
      this.analyticsRequest = this.buildAnalyticsRequest(previewSettings);
      this.analyticsRefreshToken++;
    }

    this.elasticQueryService.searchPaginatedFromLegacy(
      previewSettings.previewReport,
      setOutputFiltersPosition(previewSettings),
      this.reportsService.entityPathsToDisplay,
      this.perPage,
      null,
      null
    )
      .pipe(
        map(response => ({
          ...response,
          result: [...this.convertEntityPathKeys(response?.result)]
        })),
        tap(response => {
          this.totalRecordsFromBackend = response.totalHits ?? 0;
          this.hasMoreData = response.hasMore;
          this.allRecordsLoaded = !response.hasMore;
          this.searchAfterToken = response.searchAfterString;

          this.accumulatedData = response.result || [];
          this.accumulatedContractIds = response.contractIds || [];

          this.generatePreview({ ...response, result: this.accumulatedData });
        }),
        catchError(() => {
          this.previewLoading = false;
          this.allRecordsLoaded = false;
          this.clearLoadMoreData();
          this.cd.markForCheck();
          return EMPTY;
        }),
        finalize(() => {
          this.previewLoading = false;
          this.cd.markForCheck();
        }),
        takeUntil(this.cancelSearch$),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public loadMoreData(): void {
    if (this.hasMoreData && !this.isLoadingMore) {
      this.isLoadingMore = true;
      this.currentPage++;

      const previewSettings = this.setupPreviewContext();

      this.elasticQueryService.searchPaginatedFromLegacy(
        previewSettings.previewReport,
        setOutputFiltersPosition(previewSettings),
        this.reportsService.entityPathsToDisplay,
        this.perPage,
        this.searchAfterToken,
        this.totalRecordsFromBackend
      )
        .pipe(
          map(response => ({
            ...response,
            result: [...this.convertEntityPathKeys(response?.result)]
          })),
          tap(response => {
            this.hasMoreData = response.hasMore;
            this.allRecordsLoaded = !response.hasMore;
            this.searchAfterToken = response.searchAfterString;
            this.totalRecordsFromBackend = response.totalHits;

            this.accumulatedData = [...this.accumulatedData, ...(response.result || [])];
            this.accumulatedContractIds = [...this.accumulatedContractIds, ...(response.contractIds || [])];

            this.generatePreview({
              ...response,
              result: this.accumulatedData,
              contractIds: this.accumulatedContractIds
            });
          }),
          catchError(() => {
            this.isLoadingMore = false;
            this.hasMoreData = false;
            this.allRecordsLoaded = false;
            this.cd.markForCheck();
            return EMPTY;
          }),
          finalize(() => {
            this.isLoadingMore = false;
            this.cd.markForCheck();
          }),
          takeUntil(this.cancelSearch$),
          takeUntil(this.componentDestroyed$)
        ).subscribe();
    }
  }

  public initExport(): void {
    this.exportInProgress = true;
    const previewSettings = this.setupPreviewContext();

    this.elasticQueryService.exportFromLegacy(previewSettings.previewReport, this.reportsService.entityPathsToDisplay)
      .pipe(
        tap(document => Files.downloadBlob(document.blob, document.fileName, false)),
        finalize(() => {
          this.exportInProgress = false;
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  private setupPreviewContext(): PreviewSettings {
    const previewSettings = this.getPreviewSettings();
    return previewSettings;
  }

  public toggleAiAnalytics(enabled: boolean): void {
    this.aiAnalyticsEnabled = enabled;
    if (enabled) {
      this.analyticsRequest = this.buildAnalyticsRequest(this.setupPreviewContext());
    }
    this.cd.markForCheck();
  }

  private buildAnalyticsRequest(previewSettings: PreviewSettings): ReportAnalyticsRequest {
    const previewReport = previewSettings.previewReport;
    return {
      document: previewReport.filterDocument,
      query: this.elasticQueryService.prepareElasticQuery(previewReport, this.reportsService.entityPathsToDisplay),
      reportName: this.activeReportName || previewReport.reportName
    };
  }

  public convertEntityPathKeys(legacyResponseResults) {
    if (legacyResponseResults) {
      legacyResponseResults.forEach(result => {
        Object.keys(result).forEach(key => {
          if (key.includes(this.GP_ENTITY_PATH_SUFFIX)) {
            const newKey = key.replace(this.GP_ENTITY_PATH_SUFFIX, '');

            if (this.reportsService.entityPathsToDisplay?.includes(newKey)) {
              if (Array.isArray(result[key])) {
                result[key] = result[key].map((item: string) => item.split('/').join(' >> '));
              } else {
                result[key] = result[key].split('/').join(' >> ');
              }
            } else {
              if (Array.isArray(result[key])) {
                result[key] = result[key].map((item: string) => item.split('/').pop().trim());
              } else {
                result[key] = result[key].split('/').pop().trim();
              }
            }
            result[newKey] = result[key];
            delete result[key];
          }
        });
      });
    }
    return legacyResponseResults;
  }

  public tooMuchFiltersSet(): boolean {
    return this.report.filters.length > this.MAX_FILTER_COUNT;
  }

  public tooMuchResultsSet(): boolean {
    return this.report.columns.length > this.MAX_RESULT_COUNT;
  }

  public getPreviewSettings(): PreviewSettings {
    const type = 'preview';
    const previewReport: PreviewContractReport = this.report.getPreviewReport();
    previewReport.reportName = this.activeReportName;

    return {
      previewReport,
      type
    };
  }

  public isPreviewDisabled(): boolean {
    return !this.report.columns.length ||
      !this.report.reportEntity.beginDate ||
      !this.report.reportEntity.endDate ||
      this.tooMuchFiltersSet() ||
      this.tooMuchResultsSet();
  }

  public isExportDisabled(): boolean {
    return this.isPreviewDisabled() && !this.isPreviewLoaded;
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.reportsService.isChanged) {
      return true;
    }
    return from(
      this.modalService.dynamicConfirmModal({
        title: 'DIALOG.you_have_unsaved_changes',
        text: 'DIALOG.you_have_unsaved_changes?',
        confirmButton: 'FORMS.discard_all_changes',
        cancelButton: 'FORMS.keep_editing'
      }).then(choice => {
        if (choice === true) {
          this.reportsService.isChanged = false;
          return true;
        } else {
          return false;
        }
      })
    );
  }
}
